import { create } from 'zustand'
import api from '../services/api'

export interface Notification {
    id: number
    type: string
    title: string
    message: string
    link?: string
    is_read: boolean
    created_at: string
}

export interface TgMessage {
    id: number
    message_id?: number
    account_id?: number
    chat_id?: number
    topic_id?: number
    sender: string
    text: string
    date?: string
    lead_id?: number | null
}

interface NotificationState {
    notifications: Notification[]
    unreadCount: number
    loading: boolean
    tgMessages: TgMessage[]
    tgUnreadCount: number

    fetchNotifications: () => Promise<void>
    fetchUnreadCount: () => Promise<void>
    addNotification: (notification: Notification) => void
    markAsRead: (id: number) => Promise<void>
    markAllAsRead: () => Promise<void>
    clearAllNotifications: () => Promise<void>
    addTgMessage: (msg: TgMessage) => void
    clearTgUnread: () => void
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
    notifications: [],
    unreadCount: 0,
    loading: false,
    tgMessages: [],
    tgUnreadCount: 0,

    fetchNotifications: async () => {
        set({ loading: true })
        try {
            const response = await api.get<Notification[]>('/notifications')
            set({ notifications: response.data, loading: false })

            get().fetchUnreadCount()
        } catch (error) {
            console.error('Failed to fetch notifications:', error)
            set({ loading: false })
        }
    },

    fetchUnreadCount: async () => {
        try {
            const response = await api.get<{ count: number }>('/notifications/unread-count')
            set({ unreadCount: response.data.count })
        } catch (error) {
            console.error('Failed to fetch unread count:', error)
        }
    },

    addNotification: (notification: Notification) => {
        set(state => ({
            notifications: [notification, ...state.notifications],
            unreadCount: state.unreadCount + 1
        }))
    },

    addTgMessage: (msg: TgMessage) => {
        set(state => ({
            tgMessages: msg.message_id && state.tgMessages.some(existing =>
                existing.message_id === msg.message_id &&
                existing.account_id === msg.account_id &&
                existing.chat_id === msg.chat_id &&
                existing.topic_id === msg.topic_id
            )
                ? state.tgMessages
                : [msg, ...state.tgMessages].slice(0, 50),
            tgUnreadCount: msg.message_id && state.tgMessages.some(existing =>
                existing.message_id === msg.message_id &&
                existing.account_id === msg.account_id &&
                existing.chat_id === msg.chat_id &&
                existing.topic_id === msg.topic_id
            )
                ? state.tgUnreadCount
                : state.tgUnreadCount + 1,
        }))
    },

    clearTgUnread: () => {
        set({ tgUnreadCount: 0 })
    },

    markAsRead: async (id: number) => {
        try {

            set(state => {
                const notif = state.notifications.find(n => n.id === id)
                if (notif && !notif.is_read) {
                    return {
                        notifications: state.notifications.map(n =>
                            n.id === id ? { ...n, is_read: true } : n
                        ),
                        unreadCount: Math.max(0, state.unreadCount - 1)
                    }
                }
                return state
            })

            await api.patch(`/notifications/${id}/read`)
        } catch (error) {
            console.error('Failed to mark notification as read:', error)

        }
    },

    markAllAsRead: async () => {
        try {
            set(state => ({
                notifications: state.notifications.map(n => ({ ...n, is_read: true })),
                unreadCount: 0
            }))
            await api.post('/notifications/read-all')
        } catch (error) {
            console.error('Failed to mark all as read:', error)
        }
    },

    clearAllNotifications: async () => {
        const previousState = get()
        try {
            set({
                notifications: [],
                unreadCount: 0,
                tgMessages: [],
                tgUnreadCount: 0
            })
            await api.delete('/notifications')
        } catch (error) {
            console.error('Failed to clear notifications:', error)
            set({
                notifications: previousState.notifications,
                unreadCount: previousState.unreadCount,
                tgMessages: previousState.tgMessages,
                tgUnreadCount: previousState.tgUnreadCount
            })
        }
    }
}))
